import { initializeSocket, disconnectSocket } from "@endpoints/socket";
import {
  fetchJobMessagesProfile,
  fetchUnseenMessageCount,
} from "@store/actions/message.action";
import store from "@store/index";
import { playNotificationSound } from "@utils/helperFunction";
import { useSnackbar } from "notistack";
import React, { createContext, useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

const SocketContext = createContext<any>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const socketRef = useRef<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { logged_in, user, token } = useSelector((state: any) => state.auth);

  useEffect(() => {
    if (logged_in && user) {
      const sessionId = localStorage.getItem("sessionId");
      if (!sessionId) {
        console.error("❌ SocketProvider: No sessionId found");
        return;
      }

      // Initialize socket
      const socket = initializeSocket();
      socketRef.current = socket;

      // Add detailed event listeners
      socket?.on("connect", () => {
        console.log(
          "✅ SocketProvider: Socket connected successfully",
          socket.id
        );
      });

      socket?.on("disconnect", (reason) => {
        console.log("🔌 SocketProvider: Socket disconnected", reason);
      });

      socket?.on("connect_error", (err) => {
        console.error("❌ SocketProvider: Connection error", err.message);
      });

      socket?.on("newMessage", (message) => {
        const currentRoute = window.location.pathname;
        if (!currentRoute?.includes("messages")) {
          console.log("onMessage", message);
          playNotificationSound();
          enqueueSnackbar(`Message : ${message?.message}`, {
            variant: "success",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        }
        store.dispatch(fetchJobMessagesProfile());
        store.dispatch(fetchUnseenMessageCount());
        console.log("currentRoute", currentRoute);
      });

      // Debug all incoming events
      socket?.onAny((event, ...args) => {
        if (event !== "pong") {
          // Filter out pong to reduce noise
          console.log(`📨 SocketProvider: Received event [${event}]`, args);
        }
      });

      return () => {
        console.log("🧹 SocketProvider: Cleaning up socket listeners");
        if (socketRef.current) {
          socketRef.current.removeAllListeners();
        }
      };
    } else {
      console.log(
        "🚫 SocketProvider: User not logged in, disconnecting socket"
      );
      disconnectSocket();
      socketRef.current = null;
    }
  }, [logged_in, user?._id]); // Re-run when user ID changes

  return (
    <SocketContext.Provider value={socketRef}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
